import React, { useId, useRef, useState } from 'react';
import { CreateInstitutionRequest } from '@/models';
import { resolveAssetUrl } from '@/utils/resolveAssetUrl';


interface InstitutionLogoFieldProps {
  name: 'logoIconUrl' | 'logoFullUrl';
  label: string;
  mode: 'create' | 'edit';
  value?: string;
  helperText: string;
  onChange: (field: keyof CreateInstitutionRequest, value: string) => void;
}

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });

export const InstitutionLogoField: React.FC<InstitutionLogoFieldProps> = ({
  name,
  label,
  mode,
  value,
  helperText,
  onChange,
}) => {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | null) => {
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Only image files are supported.');
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      onChange(name, dataUrl);
      setError(null);
    } catch {
      setError('Unable to load the selected image.');
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    void handleFile(event.dataTransfer.files?.[0] || null);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    void handleFile(event.target.files?.[0] || null);
    event.target.value = '';
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    onChange(name, event.target.value);
  };

  return (
    <div>
      <label htmlFor={inputId} className="form-label">
        {label}
      </label>

      {mode === 'edit' && value && (
        <div className="border rounded-3 bg-light p-3 mb-3">
          <div className="small text-muted mb-2">Current preview</div>
          <div className="border rounded bg-white d-flex align-items-center justify-content-center overflow-hidden">
            <img
              src={resolveAssetUrl(value)}
              alt={`${label} current preview`}
              style={{ width: '100%', maxHeight: '120px', objectFit: 'contain' }}
            />
          </div>
        </div>
      )}

      <div
        className={`border rounded-3 p-3 ${isDragging ? 'border-primary bg-light' : 'bg-body-tertiary'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="d-flex flex-column flex-sm-row gap-3 align-items-sm-center">
          <div
            className="border rounded d-flex align-items-center justify-content-center overflow-hidden bg-white flex-shrink-0"
            style={{ width: '96px', height: '96px' }}
          >
            {value ? (
              <img
                src={resolveAssetUrl(value)}
                alt={`${label} preview`}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <i className="fas fa-image text-muted fs-3" aria-hidden="true"></i>
            )}
          </div>

          <div className="flex-grow-1">
            <div className="fw-semibold mb-1">
              {value ? 'Drop a replacement image here' : 'Drag and drop an image here'}
            </div>
            <div className="text-muted small mb-3">{helperText}</div>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </button>
              {value && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => onChange(name, '')}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={handleFileInputChange}
        />

        <div className="mt-3">
          <label htmlFor={inputId} className="form-label small text-muted mb-1">
            Or paste image URL
          </label>
          <input
            type="url"
            id={inputId}
            name={name}
            className="form-control"
            value={resolveAssetUrl(value) || ''}
            onChange={handleUrlChange}
            placeholder="https://example.com/logo.png"
          />
        </div>

        {error && <div className="text-danger small mt-2">{error}</div>}
      </div>
    </div>
  );
};
